import { useEffect } from "react";
import { API } from "@/lib/api";
import { isPlatformHost } from "@/context/BrandContext";

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (pathOrUrl.startsWith("/")) return `${origin}${pathOrUrl}`;
  return `${origin}/${pathOrUrl}`;
}

/** Apply SEO / Open Graph tags from /settings/public (platform hosts). */
export function applyDocumentSeo(seo, branding = {}) {
  if (!seo) return;
  const title = seo.meta_title || branding.website_title || "";
  const description = seo.meta_description || branding.slogan || "";
  const ogTitle = seo.og_title || title;
  const ogDescription = seo.og_description || description;
  const siteName = branding.product_name || "";
  const image = absoluteUrl(seo.og_image_url);
  const canonical = seo.canonical_url || (typeof window !== "undefined" ? `${window.location.origin}/` : "");

  if (title) document.title = title;
  upsertMeta("name", "description", description);
  if (seo.meta_keywords) upsertMeta("name", "keywords", seo.meta_keywords);
  upsertMeta("name", "robots", seo.robots || "index,follow");

  upsertMeta("property", "og:type", "website");
  if (siteName) upsertMeta("property", "og:site_name", siteName);
  upsertMeta("property", "og:title", ogTitle);
  upsertMeta("property", "og:description", ogDescription);
  if (canonical) upsertMeta("property", "og:url", canonical);
  if (image) {
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:secure_url", image);
  }

  upsertMeta("name", "twitter:card", seo.twitter_card || "summary_large_image");
  upsertMeta("name", "twitter:title", ogTitle);
  upsertMeta("name", "twitter:description", ogDescription);
  if (image) upsertMeta("name", "twitter:image", image);

  if (canonical) upsertLink("canonical", canonical);
}

/**
 * Loads platform SEO on boot and keeps document head in sync.
 * Social crawlers are also served a static OG HTML page via nginx → share-page.
 */
export default function SeoMeta() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isPlatformHost(window.location.hostname)) return;

    const urls = ["/api/settings/public", `${API}/settings/public`];
    let cancelled = false;

    (async () => {
      for (const url of urls) {
        try {
          const r = await fetch(url, { credentials: "omit" });
          if (!r.ok) continue;
          const data = await r.json();
          if (cancelled) return;
          if (data?.seo) applyDocumentSeo(data.seo, data.branding || {});
          return;
        } catch {
          /* try next */
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return null;
}
